import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { StyleSheet, useWindowDimensions } from 'react-native';
import { SharedValue } from 'react-native-reanimated';

import { useNavigation } from '@react-navigation/native';

import { Button } from '@/components/ButtonComponent';
import { Box } from '@/components/lib/components/Box';
import { ds } from '@/components/lib/design.system';
import { textVariants } from '@/components/lib/typography';
import { useShowOneTime } from '@/hooks/use-show-one-time';
import { useUserStore } from '@/hooks/use-user-store';
import { designSystem } from '@/styles/styles';
import { RaceToPlaceEntry } from '@/types/socialFeed.generated';
import { MAX_WEB_WIDTH, isWeb } from '@/utils/constants-platform-specific';

import { RaceToPlaceCard } from './RaceToPlaceCard';
import { RaceToPlaceLobbyList } from './RaceToPlaceLobbyList';
import { useRaceToPlaceSheet } from './RaceToPlaceProvider';
import { useRaceToPlaceInfoSheet } from './hooks/use-race-to-place-info-sheet';
import { useRaceToPlacePicks } from './hooks/use-race-to-place-picks';
import { RACE_TO_PLACE_ACTION_BUTTON_HEIGHT, RACE_TO_PLACE_LOBBY_HEIGHT } from './race-to-place-constants';

type RaceToPlaceLobbyProps = {
    raceToPlaceEntry: RaceToPlaceEntry;
    cardHeight: SharedValue<number>;
};

export const RaceToPlaceLobby = memo(({ raceToPlaceEntry, cardHeight }: RaceToPlaceLobbyProps) => {
    const { showRaceToPlaceSheet } = useRaceToPlaceSheet();
    const showInfoSheet = useRaceToPlaceInfoSheet();
    const navigation = useNavigation();
    const { show, handleInteraction } = useShowOneTime('RaceToPlace_InfoSheet');
    const infoSheetTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    useEffect(() => () => clearTimeout(infoSheetTimeoutRef.current), []);

    const { width } = useWindowDimensions();
    const profile = useUserStore(state => state.profile);
    const guest = useUserStore(state => state.guest);
    const { picks } = useRaceToPlacePicks(raceToPlaceEntry);
    const hasUserPlacedEntry = raceToPlaceEntry.participants?.includes(profile.sub);
    const fillMeterContainerWidth = isWeb ? Math.min(width, MAX_WEB_WIDTH) - 32 : undefined;

    useEffect(() => {
        cardHeight.value = RACE_TO_PLACE_LOBBY_HEIGHT;
    }, [cardHeight]);

    const openInfoSheet = useCallback(() => {
        if (show) {
            showInfoSheet();
            handleInteraction();
        }
    }, [show, showInfoSheet, handleInteraction]);

    const handleLobbyButtonPress = useCallback(() => {
        if (guest) {
            navigation.reset({ routes: [{ name: 'Launch' }] });
            return;
        }
        showRaceToPlaceSheet();
        // add slight delay to allow modal to open before showing info sheet
        infoSheetTimeoutRef.current = setTimeout(() => {
            openInfoSheet();
        }, 1000);
    }, [guest, navigation, openInfoSheet, showRaceToPlaceSheet]);

    const list = useMemo(() => <RaceToPlaceLobbyList picks={picks} />, [picks]);

    const action = useMemo(
        () => (
            <Box paddingHorizontal={'s16'}>
                <Button
                    label={
                        hasUserPlacedEntry
                            ? "You're already in this race!"
                            : `Add ${raceToPlaceEntry.picks.length}-pick lineup`
                    }
                    onPress={handleLobbyButtonPress}
                    textStyle={textVariants.titleMedium}
                    style={styles.actionButton}
                    disabled={hasUserPlacedEntry}
                    disableTextStyle={styles.disabledButtonText}
                    testID={'race-to-place-add-lineup-button'}
                />
            </Box>
        ),
        [hasUserPlacedEntry, raceToPlaceEntry.picks.length, handleLobbyButtonPress]
    );

    return (
        <RaceToPlaceCard
            raceToPlaceEntry={raceToPlaceEntry}
            fillMeterContainerWidth={fillMeterContainerWidth}
            list={list}
            action={action}
        />
    );
});

const styles = StyleSheet.create({
    actionButton: {
        height: RACE_TO_PLACE_ACTION_BUTTON_HEIGHT,
        paddingVertical: 10,
        backgroundColor: ds.colorValue.background.neutral.subtle,
    },
    disabledButtonText: {
        color: designSystem.colors.neutral300,
    },
});
